// Polyfill Buffer for browser-side dependencies that expect Node.js Buffer.
import { Buffer } from 'buffer';
(window as any).Buffer = Buffer;

// Register AG Grid modules (required for v35+) - must be done before any grid renders
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
ModuleRegistry.registerModules([AllCommunityModule]);

import React, { useEffect } from "react";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { createRoot } from "react-dom/client";
import App from "./App";
import "@fontsource/roboto";
import CssBaseline from "@mui/material/CssBaseline";
import { StyledEngineProvider } from "@mui/material/styles";
import { Provider as ThemeProvider } from "./framework/Contexts/ThemeContext";
import { AuthProvider } from "./framework/Contexts/AuthContext";
import { SnackbarProvider, useSnackbar } from "./framework/Contexts/SnackbarContext";
import { BrowserRouter as Router } from "react-router";
import {
  formatQueryKey,
  shouldSuppressGlobalErrorSnackbar,
  extractMeta,
  getErrorMessage,
} from "./framework/reactQueryErrorHandling";
import { tryAutoRecoverChunkFailure as attemptChunkRecovery, isLikelyChunkLoadError } from "./routing/appRouting";

const USE_STRICT_MODE = true;
const ReactMode = USE_STRICT_MODE ? React.StrictMode : React.Fragment;
const LEGACY_SW_CLEANUP_KEY = "pms_legacy_sw_cleanup_at";
const LEGACY_SW_CLEANUP_TTL_MS = 1000 * 60 * 60 * 24 * 7;

const ReactQueryDevtools = React.lazy(() =>
  import("@tanstack/react-query-devtools").then((m) => ({
    default: m.ReactQueryDevtools,
  }))
);

// Global error notification callback - set by GlobalErrorHandler component
let globalErrorNotify: ((message: string, severity: "error" | "warning") => void) | null = null;

let queuedGlobalErrors: Array<{ message: string; severity: "error" | "warning" }> = [];
const MAX_QUEUED_GLOBAL_ERRORS = 5;

export function setGlobalErrorNotify(fn: typeof globalErrorNotify) {
  globalErrorNotify = fn;
  if (globalErrorNotify && queuedGlobalErrors.length > 0) {
    const queue = queuedGlobalErrors;
    queuedGlobalErrors = [];
    queue.forEach(({ message, severity }) => globalErrorNotify?.(message, severity));
  }
}

// Global listeners catch chunk errors that escape React's Suspense boundary (e.g. errors in
// already-loaded modules that trigger dynamic sub-imports). importWithRetry handles errors
// during lazy-load resolution within React; these listeners are a defense-in-depth fallback.
const registerChunkRecoveryListeners = () => {
  const handleChunkError = (error: unknown) => {
    const recovered = attemptChunkRecovery(error);
    if (!recovered && isLikelyChunkLoadError(error)) {
      notifyUserOfGlobalError(
        "The application was updated. Please refresh the page.",
        "warning"
      );
    }
  };

  window.addEventListener("unhandledrejection", (event) => {
    handleChunkError(event.reason);
  });

  window.addEventListener("error", (event) => {
    if (event.error) {
      handleChunkError(event.error);
    }
  });
};

const cleanupLegacyServiceWorkersOnce = async () => {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  try {
    const lastCleanup = Number(localStorage.getItem(LEGACY_SW_CLEANUP_KEY) ?? 0);
    if (lastCleanup && Date.now() - lastCleanup < LEGACY_SW_CLEANUP_TTL_MS) {
      return;
    }

    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
    localStorage.setItem(LEGACY_SW_CLEANUP_KEY, String(Date.now()));

    if (registrations.length > 0) {
      console.info(`[service-worker] Unregistered ${registrations.length} legacy registrations.`);
    }
  } catch (error) {
    console.warn("[service-worker] Legacy cleanup failed:", error);
  }
};

const notifyUserOfGlobalError = (
  message: string,
  severity: "error" | "warning"
) => {
  if (globalErrorNotify) {
    globalErrorNotify(message, severity);
    return;
  }
  // Queue errors that occur before GlobalErrorHandler mounts
  if (queuedGlobalErrors.length >= MAX_QUEUED_GLOBAL_ERRORS) {
    console.warn(`Global error queue full (max ${MAX_QUEUED_GLOBAL_ERRORS}), discarding oldest error:`, queuedGlobalErrors[0]);
    queuedGlobalErrors.shift();
  }
  queuedGlobalErrors.push({ message, severity });
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      const errorMessage = getErrorMessage(error);
      console.error(`Query failed [${formatQueryKey(query.queryKey)}]:`, error);

      if (shouldSuppressGlobalErrorSnackbar(extractMeta(query))) return;

      notifyUserOfGlobalError(`Failed to load data: ${errorMessage}`, "error");
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, variables, context, mutation) => {
      const errorMessage = getErrorMessage(error);
      const mutationKey = mutation.options.mutationKey;
      console.error("Mutation failed:", { error, mutationKey, variables });

      // Only suppress global error notification if explicitly requested via meta.
      // Having a local onError is NOT sufficient - it might only log without notifying the user.
      if (shouldSuppressGlobalErrorSnackbar(extractMeta(mutation))) return;

      notifyUserOfGlobalError(`Operation failed: ${errorMessage}`, "error");
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 0,
    },
  },
});

// Component that registers the snackbar with global error handlers
function GlobalErrorHandler({ children }: { children: React.ReactNode }) {
  const snackbar = useSnackbar();

  useEffect(() => {
    setGlobalErrorNotify(snackbar.openSnackbar);
    return () => {
      setGlobalErrorNotify(null);
    };
  }, [snackbar.openSnackbar]);

  return <>{children}</>;
}

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element not found");
}
const root = createRoot(container);

registerChunkRecoveryListeners();
void cleanupLegacyServiceWorkersOnce();

root.render(
  <ReactMode>
    <QueryClientProvider client={queryClient}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider>
          <AuthProvider>
            {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
            <CssBaseline />
            <Router>
              <SnackbarProvider>
                <GlobalErrorHandler>
                  <App />
                </GlobalErrorHandler>
              </SnackbarProvider>
            </Router>
          </AuthProvider>
        </ThemeProvider>
      </StyledEngineProvider>
      {import.meta.env.DEV && (
        <React.Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} />
        </React.Suspense>
      )}
    </QueryClientProvider>
  </ReactMode>
);
